import { useState } from 'react';
import { toast } from 'react-toastify';

import notificationApi from '@/core/notifications/api/notification-api';
import { Subscription, Topic } from '@/core/notifications/model';
import {
  CoverageGap,
  describeCoverageGap,
  mergeTagFilters,
} from '@/core/notifications/model/subscription-coverage';
import FieldValuesSummary from './field-values-summary';

function toFieldValueEntries(gaps: CoverageGap[]) {
  return gaps.map(gap => ({
    field: gap.field,
    fieldName: gap.fieldName,
    values: gap.missingValues,
  }));
}

function extractErrorMessage(error: any): string {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message.join('. ');
  if (typeof message === 'string') return message;
  return 'Validation failed';
}

function ExpandConfirm({
  subscription,
  gaps,
  saving,
  onConfirm,
  onCancel,
}: {
  subscription: Subscription;
  gaps: CoverageGap[];
  saving: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="mt-2 p-2 rounded" style={{ border: '1px solid currentColor' }}>
      <div className="mb-2" style={{ fontSize: 13 }}>
        This will add <FieldValuesSummary entries={toFieldValueEntries(gaps)} /> to "
        {subscription.name}" — it will then also match other notifications that fit those values.
      </div>
      <div className="d-flex justify-content-end gap-2">
        <button className="btn btn-sm btn-outline-secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button
          className="btn btn-sm btn-tertiary text-base-light"
          onClick={onConfirm}
          disabled={saving}
        >
          {saving ? 'Working…' : 'Confirm'}
        </button>
      </div>
    </div>
  );
}

export default function CoverageMismatchList({
  topic,
  subscriptions,
  tagFilter,
  onExpanded,
  onCreateNew,
}: {
  topic: Topic;
  subscriptions: Subscription[];
  tagFilter: Record<string, unknown>;
  onExpanded: () => void;
  onCreateNew: () => void;
}) {
  const [expandingId, setExpandingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleExpand = async (subscription: Subscription) => {
    setSaving(true);
    setErrorMessage(null);
    try {
      await notificationApi.updateSubscription(subscription.id, {
        tagFilter: mergeTagFilters(subscription.tagFilter, tagFilter),
      });
      toast.success('Alarm updated');
      setExpandingId(null);
      onExpanded();
    } catch (error: any) {
      const message = extractErrorMessage(error);
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className={`mb-2 mutedText`} style={{ fontSize: 13 }}>
        None of your existing alarms for this topic cover what you're currently viewing.
      </div>

      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
        {subscriptions.map(subscription => {
          const gaps = describeCoverageGap(topic, subscription.tagFilter, tagFilter);

          return (
            <div
              key={subscription.id}
              className="mb-2 p-2 rounded"
              style={{ border: '1px solid currentColor' }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div style={{ fontWeight: 600 }}>
                    {subscription.name}
                    {subscription.paused && <span className="badge bg-secondary ms-2">Paused</span>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--bs-warning)' }}>
                    Doesn't include: <FieldValuesSummary entries={toFieldValueEntries(gaps)} />
                  </div>
                </div>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setExpandingId(subscription.id)}
                >
                  Expand this alarm
                </button>
              </div>

              {expandingId === subscription.id && (
                <ExpandConfirm
                  subscription={subscription}
                  gaps={gaps}
                  saving={saving}
                  onCancel={() => setExpandingId(null)}
                  onConfirm={() => handleExpand(subscription)}
                />
              )}
            </div>
          );
        })}
      </div>

      {errorMessage && (
        <div
          className="px-3 py-2 rounded mb-3"
          style={{
            background: 'rgba(var(--bs-danger-rgb), 0.15)',
            border: '1px solid var(--bs-danger)',
            color: 'var(--bs-danger)',
            fontSize: 13,
          }}
        >
          {errorMessage}
        </div>
      )}

      <div className="d-flex justify-content-end mt-3">
        <button className="btn btn-sm btn-tertiary text-base-light" onClick={onCreateNew}>
          Create new alarm for this view
        </button>
      </div>
    </div>
  );
}
